import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import {
  Book, Sparkle, GraduationCap, Flask, Users, ChartLineUp,
  DownloadSimple, ArrowRight, MapPin, Calendar, ArrowSquareOut,
} from "@phosphor-icons/react";
import ResearchSubmissionModal from "@/components/ResearchSubmissionModal";
import ListenTranslateBar from "@/components/ListenTranslateBar";

const TABS = [
  { key: "topics",        label: "Topics",         Icon: Book },
  { key: "latest",        label: "Latest",         Icon: Sparkle },
  { key: "projects",      label: "Projects",       Icon: Flask },
  { key: "universities",  label: "Universities",   Icon: GraduationCap },
  { key: "papers",        label: "Papers",         Icon: Book },
  { key: "citizen",       label: "Citizen Science",Icon: Users },
  { key: "participate",   label: "Participate",    Icon: ChartLineUp },
];

const TAB_TO_KIND = {
  projects: "project",
  universities: "organization",
  papers: "paper",
  participate: "participation",
};

const HERO_LEAD = `Our planet is undergoing rapid environmental change. Research helps us understand what is changing, why, what may happen next, and what we can do about it. NaturePulse brings environmental research closer to everyday people — connecting satellites, universities and laboratories with the observations of communities everywhere.`;

export default function ResearchHub() {
  const [tab, setTab] = useState("topics");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState([]);
  const [topicCounts, setTopicCounts] = useState({});
  const [activeTopic, setActiveTopic] = useState("");
  const [modalKind, setModalKind] = useState(null);

  useEffect(() => {
    http.get("/research/topics").then(({ data }) => {
      setTopics(data?.topics || []);
      setTopicCounts(data?.counts || {});
    });
  }, []);

  useEffect(() => {
    const kind = TAB_TO_KIND[tab];
    if (!kind && tab !== "latest" && tab !== "citizen") {
      setItems([]); setLoading(false); return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    if (kind) params.set("kind", kind);
    if (activeTopic) params.set("topic", activeTopic);
    http.get(`/research/list?${params.toString()}`)
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab, activeTopic]);

  const canSubmit = !!TAB_TO_KIND[tab];

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="research-hub">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          / Research · connect science with observation
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.92] tracking-tighter max-w-4xl">
          Understanding our changing natural world.
        </h1>
        <p className="mt-6 font-serif-display text-base md:text-lg text-[#3A3830] max-w-3xl leading-relaxed">
          {HERO_LEAD}
        </p>
        <div className="mt-6">
          <ListenTranslateBar text={`Research on NaturePulse.\n\n${HERO_LEAD}`} testidPrefix="research-hero" />
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/stories/sty_flagship_research_understanding_change"
            data-testid="research-read-flagship"
            className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-white/60"
          >
            <Book size={13} weight="fill" /> Read the flagship article
          </Link>
          <button
            onClick={() => setModalKind("project")}
            data-testid="research-submit-project"
            className="inline-flex items-center gap-2 rounded-full bg-[#B85F2A] text-white px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Sparkle size={13} weight="fill" /> Submit a project
          </button>
          <button
            onClick={() => setModalKind("participation")}
            data-testid="research-submit-participate"
            className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-white/60"
          >
            <Users size={13} weight="fill" /> Request observations
          </button>
          <button
            onClick={downloadCsv}
            data-testid="research-download-csv"
            className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-white/60"
          >
            <DownloadSimple size={13} weight="fill" /> Download data (CSV)
          </button>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14">
        <div className="-mx-1 overflow-x-auto pb-2" data-testid="research-tabs">
          <div className="flex gap-2 px-1 min-w-max">
            {TABS.map((T) => (
              <button
                key={T.key}
                onClick={() => { setTab(T.key); setActiveTopic(""); }}
                data-testid={`research-tab-${T.key}`}
                aria-selected={tab === T.key}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 border font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
                  tab === T.key
                    ? "bg-[#241209] text-[#F5EBDC] border-[#241209]"
                    : "bg-white border-black/15 text-[#241209] hover:border-black/40"
                }`}
              >
                <T.Icon size={12} weight="fill" /> {T.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {tab === "topics" && (
            <TopicsGrid topics={topics} counts={topicCounts} onPick={(t) => { setActiveTopic(t); setTab("latest"); }} />
          )}
          {tab === "citizen" && (
            <CitizenScience onSubmitParticipation={() => setModalKind("participation")} />
          )}
          {tab !== "topics" && tab !== "citizen" && (
            <SubmissionList
              tab={tab}
              items={items}
              loading={loading}
              activeTopic={activeTopic}
              onClearTopic={() => setActiveTopic("")}
              onSubmit={canSubmit ? () => setModalKind(TAB_TO_KIND[tab]) : null}
            />
          )}
        </div>
      </section>

      {modalKind && (
        <ResearchSubmissionModal
          kind={modalKind}
          topics={topics}
          defaultTopic={activeTopic}
          onClose={() => setModalKind(null)}
          onCreated={() => {
            setModalKind(null);
            // reload the current list so the new item appears
            const kind = TAB_TO_KIND[tab];
            if (kind) {
              const params = new URLSearchParams();
              params.set("kind", kind);
              if (activeTopic) params.set("topic", activeTopic);
              http.get(`/research/list?${params.toString()}`).then(({ data }) => setItems(data?.items || []));
            }
          }}
        />
      )}
    </main>
  );
}

function downloadCsv() {
  const url = `${API_BASE}/research/data-feed?format=csv&months=12`;
  window.open(url, "_blank");
}

function TopicsGrid({ topics, counts, onPick }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="research-topics-grid">
      {topics.map((t) => (
        <button
          key={t}
          onClick={() => onPick(t)}
          data-testid={`research-topic-${t}`}
          className="group rounded-2xl border border-black/10 bg-white p-4 text-left hover:border-[color:var(--np-brand,#B85F2A)] hover:-translate-y-0.5 transition-all"
        >
          <div className="font-serif-display text-lg leading-tight capitalize">
            {t.replace(/-/g, " ")}
          </div>
          <div className="mt-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] flex items-center justify-between">
            <span>{counts[t] || 0} entries</span>
            <ArrowRight size={11} weight="bold" className="text-[color:var(--np-brand,#B85F2A)] group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>
      ))}
    </div>
  );
}

function CitizenScience({ onSubmitParticipation }) {
  return (
    <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-10" data-testid="research-citizen">
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
        / Citizen Science
      </div>
      <h2 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
        A person with a smartphone can record change.
      </h2>
      <p className="mt-4 font-serif-display text-[15px] md:text-base leading-relaxed text-[#241209] max-w-3xl">
        A bird. A butterfly. A flowering tree. A flooded road. A shrinking pond. Coastal erosion. An invasive species. A wildfire scar. Unusual weather. One observation may have limited scientific value by itself — but thousands of observations collected systematically across years can become extremely valuable.
      </p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: "Adopt a place & rephotograph it", to: "/adopt-a-place" },
          { label: "Add an observation", to: "/observations" },
          { label: "Take the 7-day nature challenge", to: "/challenges" },
        ].map((c) => (
          <Link key={c.to} to={c.to} className="rounded-xl border border-black/10 bg-[#FBF2E1] p-4 font-serif-display text-lg leading-tight hover:border-[color:var(--np-brand,#B85F2A)] transition-colors">
            {c.label} <ArrowRight size={13} weight="bold" className="inline ml-1 text-[color:var(--np-brand,#B85F2A)]" />
          </Link>
        ))}
      </div>
      <div className="mt-6">
        <button
          onClick={onSubmitParticipation}
          data-testid="research-citizen-participate"
          className="inline-flex items-center gap-2 rounded-full bg-[#241209] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
        >
          <Users size={13} weight="fill" /> Are you a researcher? Request observations
        </button>
      </div>
    </div>
  );
}

function SubmissionList({ tab, items, loading, activeTopic, onClearTopic, onSubmit }) {
  const heading = TABS.find((t) => t.key === tab)?.label || "Latest";
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-serif-display text-2xl md:text-3xl tracking-tight">
          {heading} {activeTopic && <span className="text-[color:var(--np-brand,#B85F2A)] text-base font-mono-tech tracking-[0.24em] uppercase ml-2">· {activeTopic}</span>}
        </h2>
        <div className="flex items-center gap-2">
          {activeTopic && (
            <button onClick={onClearTopic} className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline" data-testid="research-clear-topic">
              Clear topic
            </button>
          )}
          {onSubmit && (
            <button
              onClick={onSubmit}
              data-testid={`research-submit-${tab}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#B85F2A] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90"
            >
              <Sparkle size={11} weight="fill" /> Submit
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="research-empty">
          <Sparkle size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
          <div className="font-serif-display text-2xl">Nothing yet.</div>
          <div className="mt-1 text-sm text-[#3A3830]">Be the first — hit Submit above.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="research-items">
          {items.map((it) => <SubmissionCard key={it.id} it={it} />)}
        </div>
      )}
    </div>
  );
}

function SubmissionCard({ it }) {
  return (
    <article className="rounded-2xl bg-white border border-black/10 p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors" data-testid={`research-card-${it.id}`}>
      <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1">
        <span className="capitalize">{it.kind}</span>
        {it.topic && <><span className="text-[#3A3830]">·</span><span className="capitalize">{it.topic.replace(/-/g, " ")}</span></>}
        {it.moderation_status === "pending" && <><span className="text-[#3A3830]">·</span><span className="text-[#3A3830]">pending review</span></>}
      </div>
      <h3 className="font-serif-display text-xl leading-tight">{it.title}</h3>
      <p className="mt-2 text-sm text-[#241209] line-clamp-4">{it.description}</p>
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-[#3A3830]">
        {it.submitter?.name && <span>By <b>{it.submitter.name}</b>{it.submitter.affiliation ? `, ${it.submitter.affiliation}` : ""}</span>}
        {it.region && <span className="inline-flex items-center gap-1"><MapPin size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />{it.region}</span>}
        {it.duration_days && <span className="inline-flex items-center gap-1"><Calendar size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />{it.duration_days} days</span>}
      </div>
      {(it.links || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {it.links.slice(0, 4).map((L, i) => (
            <a key={i} href={L.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[color:var(--np-brand,#B85F2A)] hover:underline" data-testid={`research-card-link-${it.id}-${i}`}>
              {L.label || L.url.replace(/^https?:\/\//, "").split("/")[0]}
              <ArrowSquareOut size={10} weight="bold" />
            </a>
          ))}
        </div>
      )}
    </article>
  );
}
